import { Handshake, Target, Users, Building, CheckCircle, XCircle } from 'lucide-react';

const Collaborations = () => {
  const collaborationModels = [
    {
      title: "Industry Partnerships",
      description: "Work with startups, enterprises, and organizations to engage early with student talent, share industry perspectives, and shape future-ready professionals through real interactions—not recruitment hype.",
      icon: <Building className="w-6 h-6" />
    },
    {
      title: "Employer & Institution Branding",
      description: "Enable organizations and colleges to build authentic visibility among students by participating in mentorship, learning initiatives, and community-led programs that genuinely add value.",
      icon: <Target className="w-6 h-6" />
    },
    {
      title: "Knowledge & Skill Exchange",
      description: "Create impact-driven learning through expert talks, technical workshops, panel discussions, and guided sessions led by industry professionals and experienced mentors.",
      icon: <Users className="w-6 h-6" />
    }
  ];

  const whatWeDo = [
    { text: "Enable authentic collaboration between students, colleges, mentors, and industry", icon: <CheckCircle className="w-5 h-5" /> },
    { text: "Provide early exposure to real-world expectations, roles, and skills", icon: <CheckCircle className="w-5 h-5" /> },
    { text: "Host mentorship-driven workshops, sessions, and discussions", icon: <CheckCircle className="w-5 h-5" /> },
    { text: "Support long-term community engagement, not one-off promotions", icon: <CheckCircle className="w-5 h-5" /> },
    { text: "Focus on learning, clarity, and career readiness", icon: <CheckCircle className="w-5 h-5" /> }
  ];

  const whatWeDontDo = [
    { text: "No placement guarantees or job promises", icon: <XCircle className="w-5 h-5" /> },
    { text: "No paid placement or commission-based hiring models", icon: <XCircle className="w-5 h-5" /> },
    { text: "No exaggerated claims or misleading outcomes", icon: <XCircle className="w-5 h-5" /> }
  ];

  const whoCanCollaborate = [
    "Colleges & Student Communities looking to enhance industry exposure",
    "Startups & Tech Companies seeking meaningful student engagement",
    "Founders & Professionals interested in mentoring or knowledge sharing",
    "Educators & Trainers aligned with practical, outcome-driven learning"
  ];

  return (
    <div className="min-h-screen bg-white py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0C7779] rounded-2xl mb-6">
            <Handshake className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-6 text-[#0F172A]">
            Bridging Students & Industry
          </h1>
          <p className="text-xl text-[#64748B] max-w-4xl mx-auto leading-relaxed">
            Building real-world connections that create value for students, educators, and industry partners. 
            GrowOn Community collaborates with colleges, technology companies, founders, and professionals to create 
            practical exposure, meaningful learning experiences, and long-term talent ecosystems—without shortcuts or false promises.
          </p>
        </div>

        {/* Collaboration Model */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-bold mb-12 text-center text-[#0F172A]">Our Collaboration Model</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {collaborationModels.map((model, index) => (
              <div key={index} className="bg-white rounded-xl border border-[#E2EFEA] p-8 hover:border-[#249E94]/40 hover:shadow-sm transition-all duration-300">
                <div className="w-14 h-14 bg-[#0C7779] rounded-lg flex items-center justify-center mb-6 text-white">
                  {model.icon}
                </div>
                <h3 className="text-xl font-bold mb-4 text-[#0F172A]">{model.title}</h3>
                <p className="text-[#64748B] leading-relaxed">{model.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* What We Do vs Don't */}
        <div className="max-w-6xl mx-auto mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* What We Do */}
            <div className="bg-white rounded-xl border border-[#E2EFEA] p-8">
              <div className="flex items-center mb-8">
                <div className="w-12 h-12 bg-[#0C7779] rounded-lg flex items-center justify-center mr-4">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A]">What We Do</h3>
              </div>
              <ul className="space-y-4">
                {whatWeDo.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-[#0C7779] mr-3 mt-1 flex-shrink-0">{item.icon}</span>
                    <span className="text-[#64748B]">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* What We Don't */}
            <div className="bg-white rounded-xl border border-[#E2EFEA] p-8">
              <div className="flex items-center mb-8">
                <div className="w-12 h-12 bg-[#E2EFEA] rounded-lg flex items-center justify-center mr-4">
                  <XCircle className="w-6 h-6 text-[#64748B]" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A]">What We Don't Do</h3>
              </div>
              <ul className="space-y-4">
                {whatWeDontDo.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-[#64748B] mr-3 mt-1 flex-shrink-0">{item.icon}</span>
                    <span className="text-[#64748B]">{item.text}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 p-6 bg-[#F4FAF9] rounded-xl border border-[#E2EFEA]">
                <p className="text-[#0C7779] text-center font-semibold">
                  We believe trust is built through transparency, not marketing language.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Who Can Collaborate */}
        <div className="max-w-4xl mx-auto mb-20">
          <div className="bg-gradient-to-br from-white to-[#F4FAF9] rounded-2xl p-8 border border-[#E2EFEA]">
            <h3 className="text-2xl font-bold mb-6 text-[#0F172A] text-center">Who Can Collaborate With Us</h3>
            <ul className="space-y-4">
              {whoCanCollaborate.map((item, index) => (
                <li key={index} className="flex items-start">
                  <div className="w-6 h-6 bg-[#3BC1A8]/20 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                    <div className="w-2 h-2 bg-[#0C7779] rounded-full"></div>
                  </div>
                  <span className="text-[#64748B]">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-white to-[#F4FAF9] rounded-2xl p-8 md:p-12 text-center border border-[#E2EFEA]">
            <h3 className="text-2xl md:text-3xl font-bold mb-6 text-[#0F172A]">Partner With GrowOn Community</h3>
            <p className="text-[#64748B] mb-8 max-w-2xl mx-auto leading-relaxed">
              If you believe in honest collaboration, practical learning, and building real career pathways, 
              we would love to work together. Let's create opportunities that benefit students while adding genuine value to partners.
            </p>
            
            <div className="space-y-4">
              <a 
                href="mailto:growoncommunity@gmail.com" 
                className="inline-block px-8 py-4 bg-[#249E94] text-white rounded-lg font-semibold hover:bg-[#1D867C] transition-colors text-lg"
              >
                Email: growoncommunity@gmail.com
              </a>
              <p className="text-[#64748B] text-sm mt-4">
                We typically respond within <span className="font-semibold text-[#0C7779]">24–48 hours</span>.
              </p>
            </div>

            <div className="mt-8 pt-8 border-t border-[#E2EFEA]">
              <p className="text-[#64748B] text-sm italic">
                A student-led mentorship ecosystem focused on clarity, skills, and real-world growth.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Collaborations;